import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MapPin, Package, ShieldCheck, Store, Truck, XCircle, Factory } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Public-facing batch traceability page. Rendered from `/qr/batch/:id` — no
 * authentication required. This is what a customer sees when they scan the
 * QR code on a Bakemill cake box.
 */
export default function BatchTracePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/qr/batch/${encodeURIComponent(id)}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Batch not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <TraceShell><div className="text-center text-[#8B7355] p-12">Verifying batch…</div></TraceShell>;
  if (err) return (
    <TraceShell>
      <div className="text-center p-8">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <div className="font-[Outfit] text-xl font-semibold text-red-700">Batch not found</div>
        <div className="text-sm text-[#8B7355] mt-1">{err}</div>
        <div className="mt-4 text-xs text-[#8B7355]">
          This QR code may have been tampered with. Please report to
          the retailer or email us at <a className="text-[#C05621] underline" href="mailto:hello@bakemill.com">hello@bakemill.com</a>.
        </div>
      </div>
    </TraceShell>
  );

  const producedAt = data.produced_at ? new Date(data.produced_at) : null;
  const expiryAt = data.expiry_at ? new Date(data.expiry_at) : null;
  const daysToExpiry = expiryAt ? Math.round((expiryAt - new Date()) / (86400000)) : null;
  const qcPass = (data.quality_checks || []).some(
    (q) => String(q.status || "").toLowerCase() === "passed" || String(q.status || "").toLowerCase() === "pass"
  );

  return (
    <TraceShell company={data.company}>
      {/* Auth banner */}
      <div className="rounded-lg p-4 bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="trace-verified-banner">
        <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
        <div>
          <div className="font-[Outfit] text-lg font-semibold text-emerald-900">Authentic product</div>
          <div className="text-xs text-emerald-800">
            This code has been verified against {data.company.name}'s central production database.
          </div>
        </div>
      </div>

      {/* Product hero */}
      <section className="p-4 bg-white rounded-lg border border-[#EAE5D9] mt-4">
        <div className="flex gap-3">
          {data.product.image_url ? (
            <img src={data.product.image_url} alt={data.product.name}
              className="w-24 h-24 rounded-md object-cover border border-[#EAE5D9]" />
          ) : (
            <div className="w-24 h-24 rounded-md bg-[#FEF9EA] border border-[#EAE5D9] flex items-center justify-center text-[#C05621]">
              <Package className="w-8 h-8" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{data.product.category}</div>
            <div className="font-[Outfit] text-xl font-semibold text-[#5C4033] truncate" data-testid="trace-product-name">
              {data.product.name}
            </div>
            <div className="text-[11px] text-[#8B7355]">SKU: {data.product.sku}</div>
            {data.product.description && (
              <div className="text-xs text-[#5C4033] mt-1 line-clamp-2">{data.product.description}</div>
            )}
          </div>
        </div>
      </section>

      {/* Batch details */}
      <section className="p-4 bg-white rounded-lg border border-[#EAE5D9] mt-3 space-y-3 text-sm" data-testid="trace-batch-info">
        <Row label="Batch No." value={<span className="font-mono">{data.batch_no}</span>} />
        {producedAt && <Row label="Manufactured" value={producedAt.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })} />}
        {expiryAt && (
          <Row label="Best before" value={
            <span className={daysToExpiry < 0 ? "text-red-600 font-semibold" : daysToExpiry < 3 ? "text-amber-700 font-semibold" : ""}>
              {expiryAt.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
              {daysToExpiry !== null && (
                <span className="ml-2 text-[11px] text-[#8B7355]">
                  ({daysToExpiry < 0 ? `${-daysToExpiry} day(s) past` : `${daysToExpiry} day(s) left`})
                </span>
              )}
            </span>
          } />
        )}
        {data.manufactured_by?.name && (
          <Row label="Manufactured at" value={
            <div>
              <div className="flex items-center gap-1.5"><Factory className="w-3.5 h-3.5" /> {data.manufactured_by.name}</div>
              {(data.manufactured_by.city || data.manufactured_by.state) && (
                <div className="text-[11px] text-[#8B7355] flex items-center gap-1"><MapPin className="w-3 h-3" />
                  {[data.manufactured_by.city, data.manufactured_by.state].filter(Boolean).join(", ")}
                </div>
              )}
              {(data.manufactured_by.fssai_number || data.manufactured_by.licence_number) && (
                <div className="text-[11px] text-[#8B7355] mt-1">
                  {data.manufactured_by.fssai_number && <>FSSAI: {data.manufactured_by.fssai_number}</>}
                  {data.manufactured_by.fssai_number && data.manufactured_by.licence_number && " · "}
                  {data.manufactured_by.licence_number && <>Licence: {data.manufactured_by.licence_number}</>}
                </div>
              )}
            </div>
          } />
        )}
      </section>

      {/* QC */}
      {data.quality_checks && data.quality_checks.length > 0 && (
        <section className="p-4 bg-white rounded-lg border border-[#EAE5D9] mt-3" data-testid="trace-qc">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className={`w-5 h-5 ${qcPass ? "text-emerald-600" : "text-amber-600"}`} />
            <div className="font-[Outfit] font-semibold">Quality checks</div>
          </div>
          <ul className="space-y-1 text-xs">
            {data.quality_checks.map((q) => {
              const pass = ["passed","pass"].includes(String(q.status || "").toLowerCase());
              return (
                <li key={q.id} className="flex items-center gap-2">
                  {pass ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-amber-600" />}
                  <span className="capitalize">{q.test_type || "General"}</span>
                  <span className="text-[#8B7355]">·</span>
                  <span className="text-[#8B7355]">{new Date(q.tested_at).toLocaleDateString()}</span>
                  <span className={pass ? "text-emerald-700 ml-auto font-medium" : "text-amber-700 ml-auto font-medium"}>
                    {String(q.status || "").toUpperCase()}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Distribution */}
      {data.distributed_to && data.distributed_to.length > 0 && (
        <section className="p-4 bg-white rounded-lg border border-[#EAE5D9] mt-3" data-testid="trace-distribution">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-[#C05621]" />
            <div className="font-[Outfit] font-semibold">Distributed to</div>
          </div>
          <ul className="text-xs space-y-1">
            {data.distributed_to.map((d, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <Store className="w-3.5 h-3.5 text-[#8B7355]" />
                {d.branch_name} <span className="text-[10px] text-[#8B7355]">· {d.branch_code}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </TraceShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-dashed border-[#EAE5D9] last:border-0">
      <div className="text-[11px] uppercase text-[#8B7355] tracking-wider flex-shrink-0 w-28">{label}</div>
      <div className="text-right text-[#5C4033]">{value}</div>
    </div>
  );
}

function TraceShell({ children, company }) {
  return (
    <div className="min-h-screen bg-[#FDFAF3]">
      <div className="max-w-md mx-auto px-4 py-6">
        <header className="mb-4 text-center">
          {company?.logo_url ? (
            <img src={company.logo_url} alt={company.name} className="h-10 mx-auto" />
          ) : (
            <div className="font-[Outfit] text-2xl font-semibold text-[#C05621]">{company?.name || "Bakemill Foods"}</div>
          )}
          <div className="uc-label mt-1 !text-[#8B7355]">Batch traceability</div>
        </header>
        {children}
        <footer className="mt-6 text-[10px] text-[#8B7355] text-center leading-relaxed">
          Powered by {company?.name || "Bakemill Foods"} · Central production database.<br />
          Suspect a fake? Email <a href={`mailto:${company?.email || "hello@bakemill.com"}`} className="underline">{company?.email || "hello@bakemill.com"}</a>
        </footer>
      </div>
    </div>
  );
}
